<script setup>
/**
 * SzAssigneeChips — chips de filtro CUMULATIVO com checkbox à esquerda.
 *
 * Itens (apenas 3):
 *   - me        → backend: assignee_type='me'
 *   - unread    → client-side: unread_count > 0
 *   - mentions  → backend: conversation_type='mention'
 *
 * Múltiplos podem estar ativos simultaneamente (filtros se acumulam).
 * Default: ['me'] (Minhas pré-selecionado).
 */
import { computed } from 'vue';

const props = defineProps({
  /** Array de keys ativas (Set serializado para compat de prop). Default ['me']. */
  activeFilters: { type: Array, default: () => ['me'] },
  counts: {
    type: Object,
    default: () => ({ me: 0, unread: 0, mentions: 0 }),
  },
});

const emit = defineEmits(['toggleFilter']);

const ORDER = [
  { key: 'me',       label: 'Minhas'    },
  { key: 'unread',   label: 'Não Lidas' },
  { key: 'mentions', label: 'Menções'   },
];

const activeSet = computed(() => new Set(props.activeFilters || []));

function isActive(key) {
  return activeSet.value.has(key);
}

function toggle(key) {
  emit('toggleFilter', key);
}

function formatCount(n) {
  if (!n || n < 0) return '0';
  return String(n);
}
</script>

<template>
  <div class="sz-chips" aria-label="Filtros de conversas">
    <div
      v-for="c in ORDER"
      :key="c.key"
      role="checkbox"
      tabindex="0"
      :aria-checked="isActive(c.key)"
      class="sz-chip"
      :class="{ 'sz-chip--on': isActive(c.key) }"
      @click="toggle(c.key)"
      @keydown.enter.prevent="toggle(c.key)"
      @keydown.space.prevent="toggle(c.key)"
    >
      <!-- Checkbox visual -->
      <span class="sz-chip-check" :class="{ 'sz-chip-check--on': isActive(c.key) }">
        <svg v-if="isActive(c.key)" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span class="sz-chip-label">{{ c.label }}</span>
      <!-- Badge só aparece se count > 0 (vem do backend; sem stat → sem badge) -->
      <span
        v-if="(counts[c.key] || 0) > 0"
        class="sz-chip-badge"
      >{{ formatCount(counts[c.key]) }}</span>
    </div>
  </div>
</template>

<style scoped>
.sz-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 4px;
  padding: 12px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
  font-family: 'Inter', system-ui, sans-serif;
}
.dark .sz-chips {
  background: #191B1E;
  border-bottom-color: #303138;
}
.sz-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px 0 4px;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  background: #FFFFFF;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
  user-select: none;
  box-sizing: border-box;
  /* Wrap mais previsível: gaps consistentes mesmo com badge intermitente */
  flex: 0 0 auto;
}
.sz-chip:hover { background: rgba(56,142,60,0.06); }
.sz-chip--on {
  background: #DFEFE3;
  border-color: transparent;
}
.sz-chip--on:hover { background: #D2E7D6; }
.sz-chip:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}

/* Checkbox redondo (Figma) */
.sz-chip-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid #B5B5B5;
  border-radius: 999px;
  background: transparent;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}
.sz-chip-check--on {
  background: #59AA59;
  border-color: #59AA59;
  color: #FFFFFF;
}

.sz-chip-label {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
}
.sz-chip-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 24px;
  background: #000000;
  color: #FFFFFF;
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  line-height: 20px;
}

/* Dark mode (cores do Figma) */
.dark .sz-chip {
  background: transparent;
  border-color: #3D3F43;
}
.dark .sz-chip:hover { background: rgba(255,255,255,0.04); }
.dark .sz-chip--on {
  background: #3D3F43;
  border-color: transparent;
}
.dark .sz-chip--on:hover { background: #4A4D52; }
.dark .sz-chip-label { color: #9797A0; }
.dark .sz-chip--on .sz-chip-label { color: #FFFFFF; }
.dark .sz-chip-check { background: transparent; border-color: #3D3F43; }
.dark .sz-chip-check--on { background: #59AA59; border-color: #59AA59; color: #1E1E1E; }
.dark .sz-chip-badge {
  background: #3D3F43;
  color: #BFBFC5;
}
.dark .sz-chip--on .sz-chip-badge {
  background: #5F636B;
  color: #FFFFFF;
}
</style>
